<template>
  <Dialog
    v-model:visible="isDemoSettingsVisible"
    modal
    :style="{ width: '560px' }"
    :draggable="false"
    :maximizable="true"
    @hide="onClose"
  >
    <template #header>
      <div class="flex align-items-center gap-2">
        <i class="fa-light fa-wand-magic-sparkles" />
        <span class="font-semibold">Configuration Demo</span>
      </div>
    </template>

    <div class="demo-settings-form">
      <p class="text-sm text-color-secondary mb-3">
        Ces informations remplacent temporairement les données du compte pendant la démo.
        Laissez un champ vide pour conserver la valeur réelle.
      </p>

      <!-- Shop overrides -->
      <div class="field mb-3">
        <label for="demo-shop-name" class="block text-sm font-medium mb-1">Nom de la boutique</label>
        <InputText id="demo-shop-name" v-model="form.name" class="w-full" placeholder="{var=shop.name}" />
      </div>

      <div class="field mb-3">
        <label for="demo-shop-url" class="block text-sm font-medium mb-1">URL de la boutique</label>
        <InputText id="demo-shop-url" v-model="form.url" class="w-full" placeholder="https://www.exemple.com" />
      </div>

      <div class="field mb-3">
        <label class="block text-sm font-medium mb-1">Logo de la boutique</label>
        <div class="p-inputgroup">
          <InputText id="demo-shop-logo" v-model="form.logo_url" class="w-full" placeholder="https://..." />
          <Button icon="fa-light fa-folder-open" class="p-button-outlined" @click="openFileManager('logo_url')" />
        </div>
        <img
          v-if="form.logo_url" :src="form.logo_url" class="demo-settings-preview mt-2"
          @error="onImgError" @load="onImgLoad"
        >
      </div>

      <div class="field mb-3">
        <label class="block text-sm font-medium mb-1">Icône Push Notification</label>
        <div class="p-inputgroup">
          <InputText id="demo-shop-push-icon" v-model="form.push_icon_url" class="w-full" placeholder="https://..." />
          <Button icon="fa-light fa-folder-open" class="p-button-outlined" @click="openFileManager('push_icon_url')" />
        </div>
        <img
          v-if="form.push_icon_url" :src="form.push_icon_url"
          class="demo-settings-preview demo-settings-preview--icon mt-2"
          @error="onImgError" @load="onImgLoad"
        >
      </div>

      <!-- Campaigns selection -->
      <div class="demo-settings-divider" />

      <div class="field mb-3">
        <label class="block text-sm font-medium mb-2">
          <i class="fa-light fa-robot mr-1" />
          Campagnes Automation visibles
        </label>
        <p class="text-xs text-color-secondary mb-2">
          Cochez les campagnes à afficher en mode démo. Si aucune n'est cochée, la liste sera vide.
        </p>
        <div v-if="loadingCampaigns" class="text-sm text-color-secondary">
          Chargement...
        </div>
        <div v-else class="demo-settings-campaign-list">
          <div
            v-for="c in automationCampaigns"
            :key="c.id"
            class="demo-settings-campaign-item"
          >
            <Checkbox
              :model-value="selectedAutomationIds.includes(c.id)"
              :binary="true"
              @update:model-value="toggleCampaign('automation', c.id)"
            />
            <span class="demo-settings-campaign-name">{{ c.name }}</span>
            <span class="demo-settings-campaign-id">#{{ c.id }}</span>
          </div>
          <div v-if="automationCampaigns.length === 0" class="text-xs text-color-secondary">
            Aucune campagne automation trouvée.
          </div>
        </div>
      </div>

      <div class="field mb-3">
        <label class="block text-sm font-medium mb-2">
          <i class="fa-light fa-paper-plane mr-1" />
          Campagnes Bulk visibles
        </label>
        <p class="text-xs text-color-secondary mb-2">
          Cochez les campagnes à afficher en mode démo. Si aucune n'est cochée, la liste sera vide.
        </p>
        <div v-if="loadingCampaigns" class="text-sm text-color-secondary">
          Chargement...
        </div>
        <div v-else class="demo-settings-campaign-list">
          <div
            v-for="c in bulkCampaigns"
            :key="c.id"
            class="demo-settings-campaign-item"
          >
            <Checkbox
              :model-value="selectedBulkIds.includes(c.id)"
              :binary="true"
              @update:model-value="toggleCampaign('bulk', c.id)"
            />
            <span class="demo-settings-campaign-name">{{ c.name }}</span>
            <span class="demo-settings-campaign-id">#{{ c.id }}</span>
          </div>
          <div v-if="bulkCampaigns.length === 0" class="text-xs text-color-secondary">
            Aucune campagne bulk trouvée.
          </div>
        </div>
      </div>
      <div class="field mb-3">
        <label class="block text-sm font-medium mb-2">
          <i class="fa-light fa-palette mr-1" />
          Modèles visibles
        </label>
        <p class="text-xs text-color-secondary mb-2">
          Cochez les modèles à afficher en mode démo. Si aucun n'est coché, la liste sera vide.
        </p>
        <div v-if="loadingCampaigns" class="text-sm text-color-secondary">
          Chargement...
        </div>
        <div v-else class="demo-settings-campaign-list">
          <div
            v-for="tpl in templates"
            :key="tpl.id"
            class="demo-settings-campaign-item"
          >
            <Checkbox
              :model-value="selectedTemplateIds.includes(tpl.id)"
              :binary="true"
              @update:model-value="toggleCampaign('template', tpl.id)"
            />
            <span class="demo-settings-campaign-name">{{ tpl.name }}</span>
            <span class="demo-settings-campaign-id">#{{ tpl.id }}</span>
          </div>
          <div v-if="templates.length === 0" class="text-xs text-color-secondary">
            Aucun modèle trouvé.
          </div>
        </div>
      </div>
    </div>

    <template #footer>
      <Button label="Annuler" class="p-button-text" @click="onClose" />
      <Button label="Appliquer" class="p-button-primary" @click="onSave" />
    </template>
  </Dialog>

  <FileManager
    v-if="showFileManager"
    :display-dialog="showFileManager"
    extensions="image/*"
    @on-close-dialog="showFileManager = false"
    @on-choose-file="onChooseFile"
  />
</template>

<script lang="ts">
import { defineComponent, reactive, ref, watch } from 'vue';
import Dialog from 'primevue/dialog';
import InputText from 'primevue/inputtext';
import Button from 'primevue/button';
import Checkbox from 'primevue/checkbox';
import FileManager from '@/components/file-manager/FileManager.vue';
import { UserState } from '@/composables/User';
import { NestApiType, nestCall } from '@/composables/nestApi';
import {
  isDemoSettingsVisible,
  demoOverrides,
  demoCampaigns,
  saveDemoOverrides,
  saveDemoCampaigns,
  hideDemoSettings,
  DemoOverrides,
} from '@/composables/DemoMode';

interface CampaignItem {
  id: number;
  name: string;
}

export default defineComponent({
  name: 'DemoSettingsDialog',

  components: {
    Dialog,
    InputText,
    Button,
    Checkbox,
    FileManager,
  },

  setup() {
    const form = reactive<DemoOverrides>({
      name: '',
      url: '',
      logo_url: '',
      push_icon_url: '',
    });

    const showFileManager = ref(false);
    const fileManagerTarget = ref<'logo_url' | 'push_icon_url'>('logo_url');

    // Campaign lists
    const loadingCampaigns = ref(false);
    const automationCampaigns = ref<CampaignItem[]>([]);
    const bulkCampaigns = ref<CampaignItem[]>([]);
    const selectedAutomationIds = ref<number[]>([]);
    const selectedBulkIds = ref<number[]>([]);

    const templates = ref<CampaignItem[]>([]);
    const selectedTemplateIds = ref<number[]>([]);

    const fetchCampaigns = async () => {
      loadingCampaigns.value = true;
      const idShop = UserState.activeShop?.id;
      if (!idShop) {
        loadingCampaigns.value = false;
        return;
      }

      try {
        const [autoResult, bulkResult, tplResult] = await Promise.all([
          nestCall('get', NestApiType.CLIENT_API, '/campaigns/automations', {}, '', undefined, true, { limit: 200 }),
          nestCall('get', NestApiType.CLIENT_API, '/campaigns/bulk', {}, '', undefined, true, { limit: 200 }),
          nestCall('get', NestApiType.CLIENT_API, '/templates', {}, '', undefined, true, {
            limit: 200,
            'filters[0][field]': 'deleted',
            'filters[0][operator]': 'EQUALS',
            'filters[0][values]': 'false',
            'filters[1][field]': 'created_from_campaign',
            'filters[1][operator]': 'IS_NULL',
            'filters[1][values]': '',
            'filters[2][field]': 'id_campaign',
            'filters[2][operator]': 'IS_NULL',
            'filters[2][values]': '',
            'filters[3][field]': 'config',
            'filters[3][operator]': 'JSON_NOT_EXISTS',
            'filters[3][values]': 'origin_template_id',
          }),
        ]);

        if (autoResult?.data) {
          automationCampaigns.value = autoResult.data.map((c: any) => ({
            id: c.id,
            name: c.name || c.settings?.name || `Campagne #${c.id}`,
          }));
        }

        if (bulkResult?.data) {
          bulkCampaigns.value = bulkResult.data.map((c: any) => ({
            id: c.id,
            name: c.name || c.settings?.name || `Campagne #${c.id}`,
          }));
        }

        if (tplResult?.data) {
          templates.value = tplResult.data.map((t: any) => ({
            id: t.id,
            name: t.name || `Modèle #${t.id}`,
          }));
        }
      } catch {
        // Silently fail
      }

      loadingCampaigns.value = false;
    };

    const toggleCampaign = (type: 'automation' | 'bulk' | 'template', id: number) => {
      const list = type === 'automation' ? selectedAutomationIds : type === 'bulk' ? selectedBulkIds : selectedTemplateIds;
      const idx = list.value.indexOf(id);
      if (idx === -1) {
        list.value.push(id);
      } else {
        list.value.splice(idx, 1);
      }
    };

    // Sync form with current state when dialog opens
    watch(isDemoSettingsVisible, (visible) => {
      if (visible) {
        Object.assign(form, demoOverrides);
        selectedAutomationIds.value = [...demoCampaigns.automationIds];
        selectedBulkIds.value = [...demoCampaigns.bulkIds];
        selectedTemplateIds.value = [...demoCampaigns.templateIds];
        fetchCampaigns();
      }
    });

    const openFileManager = (target: 'logo_url' | 'push_icon_url') => {
      fileManagerTarget.value = target;
      showFileManager.value = true;
    };

    const onChooseFile = (file: any) => {
      if (file && file.url) {
        form[fileManagerTarget.value] = file.url;
      }
      showFileManager.value = false;
    };

    const onSave = () => {
      saveDemoOverrides({ ...form });
      saveDemoCampaigns({
        automationIds: [...selectedAutomationIds.value],
        bulkIds: [...selectedBulkIds.value],
        templateIds: [...selectedTemplateIds.value],
      });
      hideDemoSettings();
    };

    const onClose = () => {
      hideDemoSettings();
    };

    const onImgError = (e: Event) => {
      (e.target as HTMLImageElement).style.display = 'none';
    };

    const onImgLoad = (e: Event) => {
      (e.target as HTMLImageElement).style.display = 'block';
    };

    return {
      isDemoSettingsVisible,
      form,
      showFileManager,
      openFileManager,
      onChooseFile,
      onSave,
      onClose,
      onImgError,
      onImgLoad,
      loadingCampaigns,
      automationCampaigns,
      bulkCampaigns,
      selectedAutomationIds,
      selectedBulkIds,
      templates,
      selectedTemplateIds,
      toggleCampaign,
    };
  },
});
</script>

<style scoped lang="scss">
.demo-settings-form {
  padding: 0.5rem 0;
  max-height: 70vh;
  overflow-y: auto;
}

.demo-settings-preview {
  max-height: 48px;
  max-width: 200px;
  object-fit: contain;
  border: 1px solid #e5e7eb;
  border-radius: 4px;
  padding: 4px;
  background: #fafafa;
}

.demo-settings-preview--icon {
  max-height: 32px;
  max-width: 32px;
  border-radius: 6px;
}

.demo-settings-divider {
  height: 1px;
  background: #e5e7eb;
  margin: 1rem 0;
}

.demo-settings-campaign-list {
  max-height: 200px;
  overflow-y: auto;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  padding: 0.25rem 0;
}

.demo-settings-campaign-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.375rem 0.75rem;
  font-size: 0.8125rem;

  &:hover {
    background: #f9fafb;
  }
}

.demo-settings-campaign-name {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.demo-settings-campaign-id {
  color: #9ca3af;
  font-size: 0.75rem;
  flex-shrink: 0;
}
</style>
